import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { eventStatuses } from "../../../../utils/Constants";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  getClientEventSatisfactionSurvey,
  submitClientEventSatisfactionSurvey,
} from "../../../../network/apis/satisfactionSurvey";
import {
  CLIENT_SURVEY_CRITERIA,
  COMPANY_CRITERIA_GROUPS,
} from "../../../../components/Events/Event/Evaluations/constants";
import RatingForm from "../../../../components/Events/Event/Evaluations/RatingForm";
import CompanyEvaluationResult from "../../../../components/Events/Event/Evaluations/CompanyEvaluationResult";

const SUBMITTABLE_STATUSES = [eventStatuses.ongoing, eventStatuses.past];

const ClientSatisfactionSurvey = ({ event }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events.viewEvents.evaluationsTab;
  const surveyT = t.satisfactionSurveyForm;

  const history = useHistory();
  const [survey, setSurvey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!event?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getClientEventSatisfactionSurvey(event.id);
        if (!cancelled) setSurvey(res?.data?.data ?? null);
      } catch (e) {
        if (!cancelled) showErrorMsg(e.response?.data?.message || shared.generalErrMsg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [event, shared.generalErrMsg]);

  const handleSubmit = async ({ ratings, feedback }) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await submitClientEventSatisfactionSurvey({
        eventId: event.id,
        attributes_data: CLIENT_SURVEY_CRITERIA.map((c) => ({
          section: c.section,
          attribute_name: c.attribute_name,
          rating: ratings[c.key],
        })),
        comments: feedback,
      });
      showSuccessMsg(surveyT.submitSuccess, true);
      setSurvey(res?.data?.data ?? null);
    } catch (e) {
      showErrorMsg(e.response?.data?.message || shared.generalErrMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.push(ROUTE_PATHS.clientPortalEvents);
  };

  // Once submitted (or already on file), show the read-only result.
  if (survey) {
    return <CompanyEvaluationResult survey={survey} />;
  }

  // A survey may only be submitted for an ongoing or completed event.
  if (!SUBMITTABLE_STATUSES.includes(event?.status)) {
    return <CompanyEvaluationResult survey={null} />;
  }

  const sections = COMPANY_CRITERIA_GROUPS.map((group) => ({
    key: group.key,
    title: t.companyTab.criteriaGroups[group.key],
    criteriaKeys: group.criteriaKeys,
  }));

  return (
    <Box className="eval-applicant-form">
      <RatingForm
        sections={sections}
        criteriaLabels={t.companyTab.criteria}
        layout="columns"
        feedbackTitle={t.companyTab.suggestionsFeedback}
        feedbackPlaceholder={surveyT.feedbackPlaceholder}
        statusSectionLabel={surveyT.evaluationStatus}
        statusLabel={surveyT.statusInProgress}
        submitLabel={surveyT.submit}
        cancelLabel={surveyT.cancel}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </Box>
  );
};

ClientSatisfactionSurvey.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
};

ClientSatisfactionSurvey.defaultProps = {
  event: null,
};

export default ClientSatisfactionSurvey;
